import { Action } from "redux";
import { TrackingProfile, readTrackingCookie, setCookie } from "../../lib/accountUtils";
import { m } from '../../lib/selectorUtils';
import { createThunk } from "../../lib/storeUtils";
import { trackingDataSelector } from "../selectors";

export const LOAD_TRACKINGDATA = 'TRACKINGDATA/LOAD';
export const SAVE_TRACKINGDATA = 'TRACKINGDATA/SAVE';


export interface LoadTrackingDataAction extends Action {
  payload: Promise<TrackingProfile>;
}

export interface UpdateTrackingDataAction extends Action {
  payload: TrackingProfile;
}

/**
 * Thunk action to load tracking data from local storage and cookies.
 * This action will dispatch a LOAD_TRACKINGDATA action with the loaded data.
 * The property names are based on API data fields to keep API, cookies and local storage consistent.
 * However, these fields are changed to kebabCase when used in state
 */
export const loadTrackingDataThunk = m(createThunk(
  trackingDataSelector,
  (): LoadTrackingDataAction => {
    const trackingObject = {
      email: undefined,
      given_names: undefined,
      last_name: undefined,
      auth_status: 'impersonated',  // this will denote an error (TODO once impersonation is implemented)
      anonymous_uuid: undefined,
      uuid: undefined,
      last_updated: new Date().toISOString(),
    } as TrackingProfile;

    try {
      const cookieData = readTrackingCookie();
      // console.log('[ACTION::trackingData::loadTrackingDataThunk] Promise resolved with:', { cookieData });
      return ({
        type: LOAD_TRACKINGDATA,
        payload: Promise.resolve({
          ...trackingObject,
          ...cookieData,
        } as TrackingProfile)
      });
    } catch (error) {
      console.error('Error in loadTrackingDataThunk:', error);
      return ({
        type: LOAD_TRACKINGDATA,
        payload: Promise.resolve(trackingObject as TrackingProfile)
      });
    }
  }
));

/**
 * Thunk action to update tracking data in local storage and cookies.
 * This does not write data to API, that should be done separately.
 * @param trackingData Data to write to tracking cookie and local storage which is merged with existing data.
 * @returns UpdateTrackingDataAction
 */
export const updateTrackingData = (trackingData: TrackingProfile): UpdateTrackingDataAction => {
  let payload = {} as TrackingProfile;
  try {
    const parsedCookie = readTrackingCookie();
    // console.log('[ACTION::trackingData::updateTrackingData] Existing tracking cookie data:', trackingData, parsedCookie);

    // Get existing mandatory fields from the cookie
    const { anonymous_uuid, auth_status, is_returning } = parsedCookie;  // auth_status will be determined based on authentication presence
  
    // Get new fields from the incoming data
    const { email, given_names, last_name, roles, postcode, dob, gender, dotDigital, subscriptions } = trackingData;
    const { status } = dotDigital || { status: 'unknown' };  // Default to 'unknown' if dotDigital is not provided

    payload = {
      // Member fields being updated
      email, 
      given_names: given_names || parsedCookie.given_names,  // Preserve existing given_names if present
      last_name: last_name || parsedCookie.last_name, 
      roles, 
      postcode, 
      dob, 
      gender, 
      subscriptions,
      dotDigital_status: status,  // dotDigital status
      // Persistent tracking fields
      anonymous_uuid, 
      auth_status,
      is_returning, 
      // New timestamp
      // last_updated: new Date().toISOString(),
    };
  
    const cookiePayload = {
      ...parsedCookie,
      auth_status: email ? 'unauthenticated' : auth_status,  // Update auth_status if email is present
      email, 
      given_names: given_names || parsedCookie.given_names,  // Preserve existing given_names if present
      last_name: last_name || parsedCookie.last_name, 
      roles,
      gender,
    };

    // console.log('[ACTION::trackingData::updateTrackingData]:', { payload });
    setCookie('sas', encodeURIComponent(btoa(JSON.stringify(cookiePayload))));

    return {
      type: SAVE_TRACKINGDATA,
      payload,
    };
  } catch (error) {
    console.error('Error updating tracking data:', error);
    return {
      type: SAVE_TRACKINGDATA,
      payload: trackingData,  // Return the incoming data even on error
    };
  }
};